import React, { useState, useEffect } from 'react';
import { Modal, Button, ListGroup, Alert } from 'react-bootstrap';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    TouchSensor,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Add spinning animation CSS
const spinStyle = {
    animation: 'spin 1s linear infinite',
};

// Add the keyframes for the spin animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

// Add a mapping from rule codes to user-friendly explanations
const RULE_EXPLANATIONS = {
    min_players: 'Not enough players. Add more players to meet the minimum required.',
    max_players: 'Too many players. Remove some players to meet the maximum allowed.',
    min_men: 'Not enough men. Add more men to meet the minimum required.',
    max_men: 'Too many men. Remove some men to meet the maximum allowed.',
    min_women: 'Not enough women. The team will have an automatic out each inning after all women have kicked.',
    max_women: 'Too many women. Remove some women to meet the maximum allowed.',
    single_sex: 'This league is single-sex. You cannot mix men and women in the batting order.',
    max_consecutive_men: 'Too many men in a row. Adjust the order to avoid consecutive men.',
    max_consecutive_women: 'Too many women in a row. Adjust the order to avoid consecutive women.',
    duplicate_men: 'Duplicate men are not allowed in the roster.',
    duplicate_women: 'Duplicate women are not allowed in the roster.',
    player_not_found: 'One or more players could not be found. Please refresh and try again.',
    no_rules_configured: 'No roster rules are configured for this league/season. Please contact your league administrator.',
};
const uidFor = (playerId, slotIndex) => `${playerId}-${slotIndex}`;

const ordinal = (n) => {
    const s = ["th", "st", "nd", "rd"],
        v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const isRuleOverridable = (code, leagueRules) => {
    return (
        leagueRules &&
        Array.isArray(leagueRules.overridable_rules) &&
        leagueRules.overridable_rules.includes(code)
    );
};

function SortablePlayerItem({ player, index, isInvalid, validationError, repeatNum, hasDuplicate, onToggleDuplicate, canHaveDuplicate, isRosterFinal, onSwap, canSwap, isSwapActive, isFirstSwap, swapMode }) {
    const uid = uidFor(player.id, index);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: uid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: 'none', // Prevent scrolling while dragging on mobile
        ...(hasDuplicate && {
            boxShadow: '0 0 0 2px #0d6efd',
            borderRadius: '0.375rem'
        })
    };

    return (
        <ListGroup.Item
            ref={setNodeRef}
            style={{
                ...style,
                touchAction: 'pan-y', // Allow vertical scrolling
            }}
            className={`${isDragging ? 'bg-light' : ''} ${isInvalid ? 'border-danger bg-light' : ''}`}
        >
            <div className="row align-items-center">
                {/* Drag handle and avatar */}
                <div className="col-auto">
                    <div className="d-flex align-items-center">
                        <div
                            className="me-2"
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <div
                                {...attributes}
                                {...listeners}
                                style={{
                                    cursor: 'grab',
                                    position: 'absolute',
                                    top: '-18px',
                                    left: '-18px',
                                    right: '-18px',
                                    bottom: '-18px',
                                    zIndex: 1
                                }}
                            />
                            <i className="bi bi-grip-vertical text-muted"></i>
                        </div>
                        <div className="avatar avatar-sm">
                            <img
                                className="avatar-img rounded-circle"
                                src={
                                    player.profile_picture ||
                                    `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                }
                                alt={`${player.first_name} ${player.last_name}`}
                            />
                        </div>
                    </div>
                </div>

                {/* Player information - stacked vertically */}
                <div className="col">
                    <div className="row g-0">
                        {/* Name row */}
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                                <h6 className="mb-1 fs-6">
                                    {player.first_name} {player.last_name}

                                </h6>
                                <span
                                    className={`badge ${player.gender === 'M' ? 'bg-dark' : 'bg-danger'}`}
                                    style={{ fontSize: '0.6rem' }}
                                >
                                    {player.gender === 'M' ? 'M' : 'F'}
                                </span>
                            </div>
                        </div>

                        {/* Gender and Position row */}
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                                <small className="text-muted">
                                    Position {index + 1}
                                </small>
                                {repeatNum > 1 && (
                                    <small className="text-muted ms-2">
                                        ({ordinal(repeatNum)}&nbsp;kick)
                                    </small>
                                )}
                            </div>
                        </div>

                        {/* Validation error row */}
                        {isInvalid && validationError && (
                            <div className="col-12">
                                <small className="text-danger d-block mt-1">
                                    {validationError}
                                </small>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons - right aligned */}
                <div className="col-auto">
                    <div className="d-flex flex-column flex-sm-row align-items-end align-items-sm-center gap-2">
                        {isInvalid && (
                            <i className="bi bi-exclamation-triangle-fill text-danger"></i>
                        )}

                        {canHaveDuplicate && (
                            <div className="order-2 order-sm-1">
                                <div className="d-flex align-items-center gap-2">
                                    <span className="d-inline d-sm-none">
                                        <i className="bi bi-arrow-clockwise"></i>
                                    </span>
                                    <div className="form-check form-switch">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`duplicate-${player.id}-${index}`}
                                            checked={hasDuplicate}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                onToggleDuplicate(player.id, index);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                            disabled={isRosterFinal}
                                        />
                                        <label
                                            className={`form-check-label small ${isRosterFinal ? 'text-muted' : 'text-muted'}`}
                                            htmlFor={`duplicate-${player.id}-${index}`}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ cursor: isRosterFinal ? 'not-allowed' : 'pointer' }}
                                        >
                                            <span className="d-none d-sm-inline">Repeat</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}

                        {hasDuplicate ? (
                            // Show link symbol for duplicates
                            <button
                                className="btn btn-sm btn-primary order-1 order-sm-2"
                                style={{
                                    fontSize: '0.75rem',
                                    padding: '0.25rem 0.5rem',
                                    pointerEvents: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '2.5rem'
                                }}
                            >
                                <i className="bi bi-link-45deg"></i>
                            </button>
                        ) : (
                            // Show swap button for non-duplicates
                            <button
                                type="button"
                                className={`btn btn-sm order-1 order-sm-2 ${isSwapActive && isFirstSwap
                                    ? 'btn-warning'
                                    : isSwapActive && swapMode.firstPlayerGender === player.gender
                                        ? 'btn-secondary'
                                        : isSwapActive && swapMode.firstPlayerGender !== player.gender
                                            ? 'btn-outline-secondary disabled'
                                            : 'btn-outline-secondary'
                                    }`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSwap(player.id, index);
                                }}
                                disabled={!canSwap || isRosterFinal}
                                style={{
                                    cursor: canSwap && !isRosterFinal ? 'pointer' : 'not-allowed',
                                    fontSize: '0.75rem',
                                    padding: '0.25rem 0.5rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '2.5rem'
                                }}
                                title={isSwapActive && isFirstSwap ? 'Cancel Swap' : 'Swap with same gender'}
                            >
                                <i className="bi bi-arrow-left-right"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </ListGroup.Item>
    );
}

function SetRosterModal({ show, onHide, teamPlayers, onSave, gameId, teamId, isCommissioner }) {
    const [rosterOrder, setRosterOrder] = useState([]);
    const [validationState, setValidationState] = useState({
        isValid: true,
        error: null,
        invalidPosition: null,
        rule: null,
        canOverride: false
    });
    const [isValidating, setIsValidating] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [validationTimeout, setValidationTimeout] = useState(null);
    const [duplicateSettings, setDuplicateSettings] = useState(new Map()); // Map of playerId -> boolean (has duplicate)
    const [leagueRules, setLeagueRules] = useState(null); // League rules including duplicate settings
    const [rulesLoading, setRulesLoading] = useState(false); // Loading state for league rules
    const [isRosterFinal, setIsRosterFinal] = useState(false); // Track if roster is final
    const [swapMode, setSwapMode] = useState({ active: false, firstPlayerIndex: null, firstPlayerGender: null });
    const [linkedDuplicates, setLinkedDuplicates] = useState(new Map()); // Map of playerId -> Set of indices (linked duplicates)

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px movement before starting drag
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250, // 250ms delay for touch activation
                tolerance: 5, // 5px tolerance for touch movement
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Disable sensors if roster is final
    const finalSensors = isRosterFinal ? [] : sensors;

    // Use isCommissioner to override isRosterFinal for UI disables
    const effectiveIsFinal = isRosterFinal && !isCommissioner;

    useEffect(() => {
        // Initialize roster order - first check for existing roster, then fall back to attending players
        if (teamPlayers && gameId && teamId && show) {
            fetchExistingRoster();
        }
    }, [teamPlayers, gameId, teamId, show]);

    // Reset validation state when modal opens
    useEffect(() => {
        if (show) {
            setValidationState({
                isValid: true,
                error: null,
                invalidPosition: null,
                rule: null,
                canOverride: false
            });
            setErrorMessage(null);
            setSwapMode({ active: false, firstPlayerIndex: null, firstPlayerGender: null });
        }
    }, [show]);

    const fetchExistingRoster = async () => {
        try {
            const response = await fetch(`/api/games/${gameId}/teams/${teamId}/roster/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                // Existing roster found, load it
                const rosterData = await response.json();

                const playerMap = {};
                teamPlayers.forEach(player => {
                    playerMap[player.id] = player;
                });

                // Map the roster order to player objects
                const existingRosterOrder = rosterData.ordered_player_ids_read
                    .map(id => playerMap[id])
                    .filter(player => player); // Filter out any missing players

                // Create final roster order: existing roster players first, then remaining team players
                const finalRosterOrder = [...existingRosterOrder];

                // Add any team players that aren't in the existing roster
                teamPlayers.forEach(player => {
                    if (!finalRosterOrder.find(p => p.id === player.id)) {
                        finalRosterOrder.push(player);
                    }
                });

                // If we have an existing roster, sort all team players: roster players first (in roster order), then non-roster players
                if (existingRosterOrder.length > 0) {
                    const rosterPositionMap = {};
                    existingRosterOrder.forEach((player, index) => {
                        rosterPositionMap[player.id] = index;
                    });

                    // Sort all team players: roster players first (in roster order), then non-roster players
                    finalRosterOrder.sort((a, b) => {
                        const aInRoster = a.id in rosterPositionMap;
                        const bInRoster = b.id in rosterPositionMap;

                        if (aInRoster && bInRoster) {
                            // Both in roster, sort by roster position
                            return rosterPositionMap[a.id] - rosterPositionMap[b.id];
                        } else if (aInRoster && !bInRoster) {
                            // A in roster, B not in roster - A comes first
                            return -1;
                        } else if (!aInRoster && bInRoster) {
                            // A not in roster, B in roster - B comes first
                            return 1;
                        } else {
                            // Neither in roster, maintain original order
                            return 0;
                        }
                    });
                }

                setRosterOrder(finalRosterOrder);

                // Set roster final status
                setIsRosterFinal(rosterData.is_final || false);

                // Set up duplicate settings based on the existing roster
                const newDuplicateSettings = new Map();
                const playerCounts = {};
                const newLinkedDuplicates = new Map();

                // Count players in the existing roster to determine duplicates
                existingRosterOrder.forEach((player, index) => {
                    const playerId = player.id;
                    playerCounts[playerId] = (playerCounts[playerId] || 0) + 1;

                    if (playerCounts[playerId] > 1) {
                        // This is a duplicate, set the duplicate setting
                        newDuplicateSettings.set(playerId, true);

                        // Also maintain the linked duplicates for drag behavior
                        if (!newLinkedDuplicates.has(playerId)) {
                            newLinkedDuplicates.set(playerId, new Set());
                        }
                        const links = newLinkedDuplicates.get(playerId);

                        // Find all previous instances of this player in the existing roster
                        for (let i = 0; i < index; i++) {
                            if (existingRosterOrder[i].id === playerId) {
                                links.add(i);
                            }
                        }
                        links.add(index);
                    }
                });

                setLinkedDuplicates(newLinkedDuplicates);
                setDuplicateSettings(newDuplicateSettings);
            } else if (response.status === 404) {
                // No existing roster, use attending players in default order
                const attendingPlayers = teamPlayers.filter(player => player.attending);
                setRosterOrder(attendingPlayers);
                setIsRosterFinal(false);
            } else {
                console.error('Failed to fetch existing roster');
                // Fallback to attending players
                const attendingPlayers = teamPlayers.filter(player => player.attending);
                setRosterOrder(attendingPlayers);
                setIsRosterFinal(false);
            }
        } catch (error) {
            console.error('Error fetching existing roster:', error);
            // Fallback to attending players
            const attendingPlayers = teamPlayers.filter(player => player.attending);
            setRosterOrder(attendingPlayers);
            setIsRosterFinal(false);
        }
    };

    // Fetch league rules when modal opens
    useEffect(() => {
        if (show && gameId && teamId) {
            fetchLeagueRules();
        }
    }, [show, gameId, teamId]);

    const fetchLeagueRules = async () => {
        setRulesLoading(true);
        try {
            const response = await fetch(`/api/games/${gameId}/teams/${teamId}/roster/rules/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const rules = await response.json();
                setLeagueRules(rules);
            } else {
                console.error('Failed to fetch league rules');
            }
        } catch (error) {
            console.error('Error fetching league rules:', error);
        } finally {
            setRulesLoading(false);
        }
    };

    // Validate roster whenever rosterOrder changes (with debouncing)
    useEffect(() => {
        if (rosterOrder.length > 0 && gameId && teamId) {
            // Clear any existing timeout
            if (validationTimeout) {
                clearTimeout(validationTimeout);
            }

            // Set a new timeout to validate after 500ms of no changes
            const timeout = setTimeout(() => {
                validateRoster();
            }, 500);

            setValidationTimeout(timeout);
        }

        // Cleanup timeout on unmount
        return () => {
            if (validationTimeout) {
                clearTimeout(validationTimeout);
            }
        };
    }, [rosterOrder, gameId, teamId]);

    // Note: 400 Bad Request responses in console are expected for validation failures
    // and are not actual errors - they're just the API's way of indicating invalid data
    const validateRoster = async () => {
        if (rosterOrder.length === 0) {
            setValidationState({
                isValid: true,
                error: null,
                invalidPosition: null,
                rule: null,
                canOverride: false,
            });
            return;
        }

        setIsValidating(true);
        setErrorMessage(null);

        const headers = { "Content-Type": "application/json" };
        const csrf = getCSRFToken();
        if (csrf) headers["X-CSRFToken"] = csrf;

        const body = JSON.stringify({
            ordered_player_ids: rosterOrder.map((p) => p.id),
        });

        fetch(`/api/games/${gameId}/teams/${teamId}/roster/validate/`, {
            method: "POST",
            headers,
            body,
        })
            .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
            .then(({ ok, data }) => {
                if (ok) {
                    setValidationState({
                        isValid: true,
                        error: null,
                        invalidPosition: null,
                        rule: null,
                        canOverride: false,
                        allErrors: [],
                    });
                    return;
                }

                /* ---------- consume full error list ---------- */
                const errs = Array.isArray(data.errors) ? data.errors : [];

                const anyErrors = errs.length > 0;
                const allOverridable = errs.every(e => e.can_override);
                const firstBlockingErr = errs.find(e => !e.can_override) || errs[0];

                setValidationState({
                    isValid: !anyErrors,
                    error: firstBlockingErr ? firstBlockingErr.error : "Validation failed",
                    invalidPosition: firstBlockingErr?.invalid_position ?? null,
                    rule: firstBlockingErr?.rule ?? null,
                    canOverride: anyErrors && allOverridable,
                    allErrors: errs,
                });
            })
            .catch(() =>
                setValidationState({
                    isValid: false,
                    error: "Failed to validate roster",
                    invalidPosition: null,
                    rule: null,
                    canOverride: false,
                })
            )
            .finally(() => setIsValidating(false));
    };

    const generateAutoRoster = async () => {
        if (!gameId || !teamId) {
            setErrorMessage('Missing game or team information');
            return;
        }

        setIsGenerating(true);
        setErrorMessage(null);

        // Clear existing duplicates before generating new ones
        setDuplicateSettings(new Map());
        setLinkedDuplicates(new Map());

        try {
            const headers = {
                'Content-Type': 'application/json',
            };

            const csrfToken = getCSRFToken();
            if (csrfToken) {
                headers['X-CSRFToken'] = csrfToken;
            }

            const response = await fetch(`/api/games/${gameId}/teams/${teamId}/roster/auto/`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    save: false // Don't save, just get the order
                })
            });

            const data = await response.json();

            if (response.ok && data.ordered_player_ids) {
                // Map the player IDs back to player objects
                const playerMap = {};
                teamPlayers.forEach(player => {
                    playerMap[player.id] = player;
                });

                const newRosterOrder = data.ordered_player_ids
                    .map(id => playerMap[id])
                    .filter(player => player); // Filter out any missing players

                setRosterOrder(newRosterOrder);

                // Auto-link duplicates that were generated
                const newLinkedDuplicates = new Map();
                const playerCounts = {};
                const newDuplicateSettings = new Map();

                newRosterOrder.forEach((player, index) => {
                    const playerId = player.id;
                    playerCounts[playerId] = (playerCounts[playerId] || 0) + 1;

                    if (playerCounts[playerId] > 1) {
                        // This is a duplicate, set the duplicate setting
                        newDuplicateSettings.set(playerId, true);

                        // Also maintain the linked duplicates for drag behavior
                        if (!newLinkedDuplicates.has(playerId)) {
                            newLinkedDuplicates.set(playerId, new Set());
                        }
                        const links = newLinkedDuplicates.get(playerId);

                        // Find all previous instances of this player
                        for (let i = 0; i < index; i++) {
                            if (newRosterOrder[i].id === playerId) {
                                links.add(i);
                            }
                        }
                        links.add(index);
                    }
                });

                setLinkedDuplicates(newLinkedDuplicates);
                setDuplicateSettings(newDuplicateSettings);

                // Immediately validate the new roster order
                setTimeout(() => {
                    validateRoster();
                }, 100);
            } else {
                setErrorMessage(data.error || 'Failed to generate auto roster');
            }
        } catch (error) {
            setErrorMessage('Failed to generate auto roster. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const getCookie = (name) => {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    };

    const getCSRFToken = () => {
        // Try to get CSRF token from cookie
        let token = getCookie('csrftoken');

        // If not in cookie, try to get from meta tag
        if (!token) {
            const metaTag = document.querySelector('meta[name="csrf-token"]');
            if (metaTag) {
                token = metaTag.getAttribute('content');
            }
        }

        return token;
    };

    const handleToggleDuplicate = (playerId, index) => {
        setDuplicateSettings(prev => {
            const newSettings = new Map(prev);
            const currentHasDuplicate = newSettings.get(playerId) || false;

            if (currentHasDuplicate) {
                // Remove duplicate
                newSettings.set(playerId, false);
                setRosterOrder(currentOrder => {
                    // Find and remove the duplicate instance
                    const newOrder = [...currentOrder];
                    const duplicateIndex = newOrder.findIndex((player, idx) =>
                        player.id === playerId && idx !== index
                    );
                    if (duplicateIndex !== -1) {
                        newOrder.splice(duplicateIndex, 1);
                    }
                    return newOrder;
                });

                // Update linkedDuplicates - remove all indices for this player
                setLinkedDuplicates(prev => {
                    const newLinked = new Map(prev);
                    newLinked.delete(playerId);
                    return newLinked;
                });
            } else {
                // Add duplicate
                newSettings.set(playerId, true);
                setRosterOrder(currentOrder => {
                    // Find the original player and add a duplicate after it
                    const newOrder = [...currentOrder];
                    const originalPlayer = newOrder[index];
                    const newIndex = index + 1;
                    newOrder.splice(newIndex, 0, { ...originalPlayer });

                    // Update linkedDuplicates - add the new index
                    setLinkedDuplicates(prev => {
                        const newLinked = new Map(prev);
                        if (!newLinked.has(playerId)) {
                            newLinked.set(playerId, new Set());
                        }
                        const indices = newLinked.get(playerId);
                        indices.add(index); // Original instance
                        indices.add(newIndex); // New duplicate instance
                        return newLinked;
                    });

                    return newOrder;
                });
            }

            // Reset focus after state updates
            setTimeout(() => {
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            }, 0);

            return newSettings;
        });
    };

    const hasPlayerDuplicate = (playerId, index) => {
        if (!linkedDuplicates.has(playerId)) return false;
        const duplicateIndices = linkedDuplicates.get(playerId);
        return duplicateIndices.has(index);
    };

    const canHavePlayerDuplicate = (playerId, repeatNum) => {
        // Check if this player appears in the roster
        const playerInRoster = rosterOrder.some(p => p.id === playerId);
        if (!playerInRoster) return false;

        // Get the player to check their gender
        const player = rosterOrder.find(p => p.id === playerId);
        if (!player) return false;

        // Only show switch for the first instance of the player (repeatNum === 1)
        if (repeatNum > 1) return false;

        // If we're loading rules or don't have league rules yet, don't show switches
        if (rulesLoading || !leagueRules) return false;

        // Check if duplicates are allowed for this specific gender based on league rules
        if (player.gender === 'M') {
            return leagueRules.allow_duplicate_men;
        } else if (player.gender === 'F') {
            return leagueRules.allow_duplicate_women;
        }

        return false;
    };

    const getDuplicateIndices = (playerId) => {
        const indices = [];
        rosterOrder.forEach((player, index) => {
            if (player.id === playerId) {
                indices.push(index);
            }
        });
        return indices;
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setRosterOrder((items) => {
                // Extract player IDs from the uids (format: "playerId-index")
                const activePlayerId = active.id.split('-')[0];
                const overPlayerId = over.id.split('-')[0];

                const oldIndex = items.findIndex((item, idx) => uidFor(item.id, idx) === active.id);
                const newIndex = items.findIndex((item, idx) => uidFor(item.id, idx) === over.id);

                // Check if the dragged item is a duplicate
                const draggedItem = items[oldIndex];

                // Count how many instances of this player come before the current index
                let repeatNum = 1;
                for (let i = 0; i < oldIndex; i++) {
                    if (items[i].id === draggedItem.id) {
                        repeatNum++;
                    }
                }

                const isDuplicate = repeatNum > 1;



                // Move only the single dragged item
                const newOrder = arrayMove(items, oldIndex, newIndex);

                // Only remove duplicates if the ORIGINAL item is moved (not the duplicate)
                if (!isDuplicate) {
                    // Check if this player has duplicates enabled
                    const hasDuplicateEnabled = duplicateSettings.get(draggedItem.id);

                    if (hasDuplicateEnabled) {



                        // Remove duplicate setting
                        setDuplicateSettings(prev => {
                            const newSettings = new Map(prev);
                            newSettings.set(draggedItem.id, false);
                            return newSettings;
                        });

                        // Remove linked duplicates
                        setLinkedDuplicates(prev => {
                            const newLinked = new Map(prev);
                            newLinked.delete(draggedItem.id);
                            return newLinked;
                        });

                        // Remove the duplicate instance from the roster
                        const filteredOrder = newOrder.filter((p, idx) => {
                            if (p.id === draggedItem.id) {
                                // Keep only the first instance (the original that was moved)
                                const firstInstanceIndex = newOrder.findIndex(player => player.id === draggedItem.id);
                                return idx === firstInstanceIndex;
                            }
                            return true;
                        });


                        return filteredOrder;
                    }
                } else {
                    // Duplicate was moved - update the linkedDuplicates to reflect the new position


                    setLinkedDuplicates(prev => {
                        const newLinked = new Map(prev);
                        if (newLinked.has(draggedItem.id)) {
                            const indices = new Set();

                            // Find all instances of this player in the new order
                            newOrder.forEach((player, idx) => {
                                if (player.id === draggedItem.id) {
                                    indices.add(idx);
                                }
                            });

                            newLinked.set(draggedItem.id, indices);
                        }
                        return newLinked;
                    });
                }

                return newOrder;
            });
        }
    };

    const handleSave = () => {
        onSave(rosterOrder);
        onHide();
    };

    const getValidationErrorForPosition = (position) => {
        if (validationState.invalidPosition === position) {
            return validationState.error;
        }
        return null;
    };
    const appearanceCount = {};

    const handleSwap = (playerId, index) => {
        const player = rosterOrder[index];
        const playerGender = player.gender;

        if (!swapMode.active) {
            // Start swap mode - enable switches for same gender players
            setSwapMode({
                active: true,
                firstPlayerIndex: index,
                firstPlayerGender: playerGender
            });
        } else if (swapMode.firstPlayerIndex === index) {
            // Clicked the same player - cancel swap mode
            setSwapMode({ active: false, firstPlayerIndex: null, firstPlayerGender: null });
        } else if (swapMode.firstPlayerGender === playerGender) {
            // Second player selected - perform swap
            setRosterOrder(currentOrder => {
                const newOrder = [...currentOrder];
                const temp = newOrder[swapMode.firstPlayerIndex];
                newOrder[swapMode.firstPlayerIndex] = newOrder[index];
                newOrder[index] = temp;
                return newOrder;
            });

            // Reset swap mode
            setSwapMode({ active: false, firstPlayerIndex: null, firstPlayerGender: null });
        }
    };

    const canSwap = (playerId, index) => {
        if (!swapMode.active) return true; // All players can start swap mode
        if (swapMode.firstPlayerIndex === index) return true; // Can cancel own swap
        const player = rosterOrder[index];
        return swapMode.firstPlayerGender === player.gender; // Only same gender can be swapped
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Set Roster Order</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {effectiveIsFinal && (
                    <Alert variant="warning" className="mb-3">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <strong>Roster is Final</strong> - This roster has been locked and cannot be modified because the game has started.
                    </Alert>
                )}

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted mb-0">
                        Drag and drop players to set the roster order for this game.
                        {leagueRules && !rulesLoading && (
                            <>
                                <br />
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Use the Repeat Order switch to continue the women's batting order.
                                </small>
                            </>
                        )}
                    </p>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={generateAutoRoster}
                        disabled={isGenerating || rosterOrder.length === 0 || effectiveIsFinal}
                    >
                        {isGenerating ? (
                            <>
                                <i className="bi bi-arrow-clockwise me-2" style={spinStyle}></i>
                                Generating...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-magic me-2"></i>
                                Auto Generate
                            </>
                        )}
                    </Button>
                </div>

                {errorMessage && (
                    <Alert variant="danger" className="mb-3" onClose={() => setErrorMessage(null)} dismissible>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {errorMessage}
                    </Alert>
                )}

                {!validationState.isValid && validationState.error && (
                    <Alert variant={validationState.canOverride ? "warning" : "danger"} className="mb-2">
                        <i className={`bi ${validationState.canOverride ? 'bi-exclamation-triangle' : 'bi-exclamation-triangle-fill'} me-2`}></i>
                        {validationState.canOverride ? (
                            <>
                                <strong>Roster requires commissioner approval:</strong> {validationState.error}
                                <div className="mt-1 small text-muted">
                                    This roster violates league rules but can be submitted for commissioner review.
                                    {validationState.rule && RULE_EXPLANATIONS[validationState.rule] && (
                                        <>
                                            <br />
                                            <span>{RULE_EXPLANATIONS[validationState.rule]}</span>
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                {validationState.error}
                                {validationState.rule && (
                                    <div className="mt-1 small text-muted">
                                        {RULE_EXPLANATIONS[validationState.rule] && (
                                            <>
                                                <br />
                                                <span>{RULE_EXPLANATIONS[validationState.rule]}</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </Alert>
                )}

                {validationState.isValid && rosterOrder.length > 0 && (
                    <Alert variant="success" className="mb-3">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Roster is valid!
                    </Alert>
                )}

                <DndContext
                    sensors={finalSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={rosterOrder.map((p, idx) => uidFor(p.id, idx))}
                        strategy={verticalListSortingStrategy}
                    >
                        <ListGroup style={{ touchAction: 'pan-y' }}>
                            {rosterOrder.map((player, index) => {
                                const count = (appearanceCount[player.id] ?? 0) + 1;
                                appearanceCount[player.id] = count;
                                const canSwapThisPlayer = canSwap(player.id, index);

                                return (
                                    <SortablePlayerItem
                                        key={uidFor(player.id, index)}
                                        player={player}
                                        index={index}
                                        repeatNum={count}
                                        isInvalid={validationState.invalidPosition === index + 1}
                                        validationError={getValidationErrorForPosition(index + 1)}
                                        hasDuplicate={hasPlayerDuplicate(player.id, index)}
                                        onToggleDuplicate={(playerId, idx) => {
                                            handleToggleDuplicate(playerId, idx);
                                        }}
                                        canHaveDuplicate={canHavePlayerDuplicate(player.id, count)}
                                        isRosterFinal={effectiveIsFinal}
                                        onSwap={handleSwap}
                                        canSwap={canSwapThisPlayer}
                                        isSwapActive={swapMode.active}
                                        isFirstSwap={swapMode.firstPlayerIndex === index}
                                        swapMode={swapMode}
                                    />
                                );
                            })}
                        </ListGroup>
                    </SortableContext>
                </DndContext>

                {rosterOrder.length === 0 && (
                    <div className="text-center text-muted py-4">
                        <i className="bi bi-people fs-1"></i>
                        <p className="mt-2">No attending players to set roster for.</p>
                    </div>
                )}

                {isValidating && (
                    <div className="text-center text-muted py-2">
                        <i className="bi bi-arrow-clockwise me-2" style={spinStyle}></i>
                        Validating roster...
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant={!validationState.isValid && validationState.canOverride ? "warning" : "primary"}
                    onClick={handleSave}
                    disabled={rosterOrder.length === 0 || (validationState.isValid === false && !validationState.canOverride) || isValidating || effectiveIsFinal}
                >
                    {!validationState.isValid && validationState.canOverride ? "Request Override" : "Save Roster"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SetRosterModal; 